import { DatePipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { WaterRating, WaterRatingUpdate } from '@models/water-rating';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmWatersService } from '@services/crm-waters.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { filter, firstValueFrom } from 'rxjs';
import { WaterRatingDialogComponent, WaterRatingDialogData } from './water-rating-dialog.component';

@Component({
  selector: 'app-water-ratings',
  templateUrl: './water-ratings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    UpperCasePipe,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
})
export class WaterRatingsComponent implements OnInit {
  readonly waterId = input.required<number>();
  /** Emits the ratings count so the parent reviews component can show it as a tab badge. */
  readonly totalCount = output<number>();

  private readonly watersService = inject(CrmWatersService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly ratings = signal<WaterRating[]>([]);
  readonly isLoading = signal(false);

  readonly displayedColumns = ['name', 'locale', 'arrivalDate', 'type', 'average', 'createdAt', 'actions'];

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.isLoading.set(true);
    this.watersService
      .getRatings(this.waterId())
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => [] as WaterRating[],
          this.translate.instant('waters.ratings.load-failed'),
        ),
      )
      .subscribe((items) => {
        this.ratings.set(items);
        this.isLoading.set(false);
        this.totalCount.emit(items.length);
      });
  }

  average(rating: WaterRating): string {
    const sum = rating.companyReview + rating.waterReview + rating.waterSideGuidanceReview + rating.facilitiesReview;
    return (sum / 4).toFixed(1);
  }

  edit(rating: WaterRating): void {
    const data: WaterRatingDialogData = { rating };
    this.dialog
      .open<WaterRatingDialogComponent, WaterRatingDialogData, WaterRatingUpdate>(WaterRatingDialogComponent, {
        data,
        width: '720px',
      })
      .afterClosed()
      .pipe(filter((dto): dto is WaterRatingUpdate => dto != null))
      .subscribe((dto) => {
        this.watersService.updateRating(this.waterId(), rating.id, dto).subscribe({
          next: () => {
            this.toastService.showSuccess(this.translate.instant('waters.ratings.save-success'));
            this.load();
          },
          error: () => this.toastService.showError(this.translate.instant('waters.ratings.save-failed')),
        });
      });
  }

  async delete(rating: WaterRating): Promise<void> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.ratings.delete-title'),
      prompt: this.translate.instant('waters.ratings.delete-prompt'),
    });
    if (!confirmed) return;

    try {
      await firstValueFrom(this.watersService.deleteRating(this.waterId(), rating.id));
      this.toastService.showSuccess(this.translate.instant('waters.ratings.delete-success'));
      this.load();
    } catch {
      this.toastService.showError(this.translate.instant('waters.ratings.delete-failed'));
    }
  }
}
